import React, { useEffect } from 'react'
import { AppShell } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

import SideBar from './components/nav/SideBar/Sidebar'
import TopBar from './components/nav/TopBar/TopBar'
import Login from './components/main/Login/LoginForm'
import MainContent from './components/main/MainContent'
import { useAuthStore } from '@store/auth.store'
import AppDataBootstrap from '@components/general/AppDataBootstrap/AppDataBootstrap'
import ForgotPassword from './components/main/Login/ForgotPassword'
import ConfirmEmail from './components/main/Login/ConfirmEmail'
import GlobalDetectIdle from './components/general/GlobalDetectIdle/GlobalDetectIdle'
import ResetPassword from './components/main/Login/ResetPassword'
import AccessUnavailable from '@components/main/AccessUnavailable/AccessUnavailable'
import Loading from './components/general/Loading/Loading'
import RedirectRoute from './RedirectRoute'
import * as Sentry from '@sentry/react'
import {
  AxiosInterceptor,
  getTracePropagationTargets,
} from '@api/backend'

import './App.scss'
import './common/styles'
import { stringToBool } from '@common/utils/helperFunctions'


Sentry.init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN || '',
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: getTracePropagationTargets(),
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled:
    stringToBool(import.meta.env.VITE_APP_SENTRY_ENABLED) && import.meta.env.VITE_APP_SENTRY_DSN !== '',
  environment: import.meta.env.VITE_APP_ENV_STATE,
})

export const SentryRoutes = Sentry.wrapReactRouterRouting(Routes)

const BASE_APPLICATION_PERMISSIONS = [
  'READ:ACCOUNT',
  'READ:CUST_LOCATION',
]

const App: React.FC = () => {
  const bootstrap = useAuthStore((state) => state.bootstrap)
  const authenticated = useAuthStore((state) => state.authenticated)
  const loading = useAuthStore((state) => state.loading)
  const [navbarOpened, { toggle: toggleNavbar, close: closeNavbar }] = useDisclosure(false)
  const canAccessApplication = useAuthStore((state) =>
    BASE_APPLICATION_PERMISSIONS.every((requiredPermission) =>
      state.permissions.some((permission) => permission.name === requiredPermission),
    ),
  )

  useEffect(() => {
    bootstrap()
  }, [])

  if (loading) {
    return <Loading />
  }

  return (
    <BrowserRouter>
      {!authenticated ? (
        <AxiosInterceptor>
          <SentryRoutes>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/users/confirm_email/:token" element={<ConfirmEmail />} />
            <Route path="/users/register/:token" element={<ConfirmEmail />} />
            <Route path="/users/reset_password/:token" element={<ResetPassword />} />
            <Route
              path="*"
              element={
                <RedirectRoute>
                  <Navigate to="/" replace />
                </RedirectRoute>
              }
            />
          </SentryRoutes>
        </AxiosInterceptor>
      ) : !canAccessApplication ? (
        <AccessUnavailable />
      ) : (
        <GlobalDetectIdle>
          <>
            <AppDataBootstrap />
            <AppShell
              // 8rem below `sm` because the TopBar selectors stack there and the test-environment
              // banner shares the same box. This breakpoint must stay in sync with the Flex
              // `direction` breakpoint in TopBar.tsx — they are one decision written twice.
              header={{ height: { base: '8rem', sm: '6rem' } }}
              navbar={{
                width: '18rem',
                breakpoint: 'sm',
                collapsed: {
                  mobile: !navbarOpened,
                  desktop: !navbarOpened,
                },
              }}
              padding={0}
              withBorder={false}
            >
              <AppShell.Header>
                <TopBar navbarOpened={navbarOpened} onToggleNavbar={toggleNavbar} />
              </AppShell.Header>
              <AppShell.Navbar
                style={{
                  borderInlineEnd: '1px solid var(--mantine-color-default-border)',
                  boxShadow:
                    '6px 0 16px -4px color-mix(in srgb, var(--mantine-color-black) 30%, transparent)',
                }}
              >
                <SideBar onNavigate={closeNavbar} />
              </AppShell.Navbar>
              <AppShell.Main className={'content-wrapper'}>
                <AxiosInterceptor>
                  <MainContent />
                </AxiosInterceptor>
              </AppShell.Main>
            </AppShell>
          </>
        </GlobalDetectIdle>
      )}
    </BrowserRouter>
  )
}

export default App
